import React, { useEffect, useState } from "react";
import { UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Avatar, Form } from "antd";
import { toast } from "react-toastify";
import ImgCrop from "antd-img-crop";

import { useAppDispatch, useAppSelector, setProfile } from "store";
import { Button, Input, Typography, Select } from "@atoms";
import { Notification } from "@organisms";
import EnergyIcon from "@assets/icons/energy.svg";
import { axiosInstance } from "utils";

import {
  Container,
  Parent,
  Upload,
  UploadParent,
  DividerVertical,
  Actions,
  SelectWrapper,
  ProfileWrapper,
  EnergyWrapper,
  Candle,
  Energy, InfoWrapper,
} from "./profile.styles";
import { days, months, years } from "./profile.constants";

const Profile = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const { profile } = useAppSelector((state) => state.profile);
  const { t } = useTranslation();

  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    profile?.image || null
  );
  const [isDisabled, setIsDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [date, setDate] = useState(profile?.birth_date?.split("-") || []);
  const [username, setUsername] = useState(profile?.username || "");
  const [city, setCity] = useState(profile?.city || "");
  const [energy, setEnergy] = useState(profile?.energy_count || 0);
  const [initialProfile, setInitialProfile] = useState({
    username: profile?.username || "",
    city: profile?.city || "",
    image: profile?.image || null,
    birth_date: profile?.birth_date?.split("-") || [],
  });

  useEffect(() => {
    setInitialProfile({
      username: profile?.username || "",
      city: profile?.city || "",
      image: profile?.image || null,
      birth_date: profile?.birth_date?.split("-") || [],
    });
    setEnergy(profile?.energy_count as number);
    setDate(profile?.birth_date?.split("-") || []);
  }, [profile]);

  useEffect(() => {
    form.setFieldsValue({
      username: profile?.username,
      city: profile?.city,
      email: profile?.email,
    });
    setPreviewUrl(profile?.image as string);
  }, [profile, form]);

  const handleChange = async (fileList: any) => {
    const { file } = fileList;
    const uploadedFile = file.originFileObj;

    if (uploadedFile) {
      setImage(uploadedFile);

      const reader = new FileReader();
      reader.onload = () => setPreviewUrl(reader.result as string);
      reader.readAsDataURL(uploadedFile);
      setIsDisabled(false);
    }
  };

  const handleValuesChange = async () => {
    form
      .validateFields()
      .then(() => {
        setIsDisabled(false);
      })
      .catch((err) => {
        setIsDisabled(!!err.errorFields.length);
      });
  };

  const handleSave = async () => {
    setIsLoading(true);

    try {
      const payload = Object.entries({
        username,
        city,
        image,
        birth_date: date.join("-"),
      }).reduce(
        (acc, [key, value]) => (value ? { ...acc, [key]: value } : acc),
        {}
      );

      const formData = new FormData();

      Object.entries(payload).forEach(([key, value]) => {
        if (value === "none") {
          // @ts-ignore
          formData.append(key, "");
        } else if (value) {
          // @ts-ignore
          formData.append(key, value);
        }
      });

      await axiosInstance.patch("accounts/profile/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      dispatch(setProfile({ ...profile, ...payload, image: previewUrl }));

      toast.success(t("dataSave"), {
        position: "bottom-right",
        theme: "colored",
      });
      setIsDisabled(true)
    } catch (err) {
      // @ts-ignore
      toast.error(Object.values(err.response.data)[0][0], {
        position: "bottom-right",
        theme: "colored",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeSelect = (event: string, type: string) => {
    switch (type) {
      case "year":
        setDate([event, date[1], date[2]]);
        setIsDisabled(false);
        break;
      case "month":
        setDate([date[0], event, date[2]]);
        setIsDisabled(false);
        break;
      case "day":
        setDate([date[0], date[1], event]);
        setIsDisabled(false);
        break;
      default:
        break;
    }
  };

  const handleCancel = () => {
    setUsername(initialProfile.username);
    setCity(initialProfile.city);
    setImage(null);
    setPreviewUrl(initialProfile.image);
    setDate(initialProfile.birth_date);
    form.setFieldsValue({
      username: initialProfile.username,
      city: initialProfile.city,
    });
    setIsDisabled(true);
  };

  const handleDeleteImage = () => {
    // @ts-ignore
    setImage("none");
    setPreviewUrl(null);
    setIsDisabled(false);
  };

  return (
    <Container>
      <ProfileWrapper>
        <Typography as="h4" style={{ color: "#391D0A" }} className="title">
          {t("basicInfo")}
        </Typography>
        <Parent>
          <div>
            <ImgCrop
              modalTitle={t("editImage")}
              modalOk={t("done")}
              modalCancel={t("cancel")}
              cropShape="round"
              rotationSlider={false}
            >
              <UploadParent
                maxCount={1}
                onChange={handleChange}
                accept="image/png, image/jpeg"
              >
                <Upload>
                  <Avatar
                    size={148}
                    icon={
                      previewUrl ? (
                        <img src={previewUrl} alt="avatar" />
                      ) : (
                        <UserOutlined />
                      )
                    }
                    style={{ border: "2px solid #C6A691" }}
                  />
                  <Typography style={{ color: "#C6A691" }}>
                    {previewUrl ? "Заменить фото" : t("addImage")}
                  </Typography>
                </Upload>
              </UploadParent>
            </ImgCrop>
            <Typography
              style={{
                color: "#BFBFBF",
                textAlign: "center",
                cursor: "pointer",
              }}
              onClick={handleDeleteImage}
            >
              {t("delete")}
            </Typography>
          </div>

          <DividerVertical />

          <Form
            style={{ width: "100%" }}
            form={form}
            layout="vertical"
            onValuesChange={handleValuesChange}
            requiredMark={false}
          >
            <Form.Item label="Email" name="email">
              <Input disabled placeholder={t("writeEmail")} />
            </Form.Item>

            <Form.Item label={t("name")} name="username">
              <Input onChange={setUsername} placeholder={t("writeName")} />
            </Form.Item>

            <Form.Item
              label={t("dateofbirth")}
              name="birthday"
              className="date-select"
              style={{ display: "flex" }}
            >
              <SelectWrapper>
                <Select
                  value={
                    // @ts-ignore
                    date[2] || profile?.birth_date?.split("-")[2] || t("day")
                  }
                  onChange={(event: string) => handleChangeSelect(event, "day")}
                  style={{ width: 120 }}
                  options={days}
                />
                <Select
                  // @ts-ignore
                  value={
                    months[
                      // @ts-ignore
                      date[1] || profile?.birth_date?.split("-")[1]
                    ] || t("month")
                  }
                  onChange={(event: string) =>
                    handleChangeSelect(event, "month")
                  }
                  style={{ width: 120 }}
                  options={months}
                />
                <Select
                  value={
                    date[0] || profile?.birth_date?.split("-")[0] || t("year")
                  }
                  onChange={(event: string) =>
                    handleChangeSelect(event, "year")
                  }
                  style={{ width: 120 }}
                  options={years}
                />
              </SelectWrapper>
            </Form.Item>

            <Form.Item label={t("city")} name="city">
              <Input
                value={city}
                onChange={setCity}
                placeholder={t("writeCity")}
              />
            </Form.Item>
          </Form>
        </Parent>
        <Actions>
          <Button
            style={{ height: "42px", display: isDisabled ? "none" : "flex" }}
            variant="outlined"
            onClick={handleCancel}
          >
            {t("cancel")}
          </Button>
          <Button
            style={{ height: "42px" }}
            disabled={isDisabled}
            loading={isLoading}
            onClick={handleSave}
          >
            {t("save")}
          </Button>
        </Actions>
      </ProfileWrapper>

      <InfoWrapper>

        <EnergyWrapper>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography as="h4" style={{ color: "#391D0A" }}>
              {t("yourlight")}
            </Typography>
            <Candle>
              <img src={EnergyIcon} alt="candle" />
              <Typography as="h2">
                {energy}
                <span>/7</span>
              </Typography>
            </Candle>
          </div>

          <div
            style={{
              display: "grid",
              gap: "8px",
              marginTop: "32px",
              gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr 1fr",
            }}
          >
            {Array.from({ length: 7 }, (_, i) => (
              <Energy key={i} full={energy && i < energy ? true : false} />
            ))}
          </div>
        </EnergyWrapper>

        <Notification />
      </InfoWrapper>
    </Container>
  );
};

export default Profile;
